import { chakra, Flex, useColorModeValue } from '@chakra-ui/react';
import BigNumber from 'bignumber.js';
import React from 'react';

import { route } from 'nextjs-routes';

import { ZERO } from 'lib/consts';
import getCurrencyValue from 'lib/getCurrencyValue';
import { type Ticker } from 'lib/hooks/usePrice';
import TokenEntity from 'ui/shared/entities/token/TokenEntity';
import LinkInternal from 'ui/shared/links/LinkInternal';
import TruncatedValue from 'ui/shared/TruncatedValue';

import type { TokenEnhancedData } from '../utils/tokenUtils';

interface Props {
  data: TokenEnhancedData;
  getPriceToken?: (address: string) => Ticker | undefined;
}

const TokenSelectItem = ({ data, getPriceToken }: Props) => {
  const priceToken = getPriceToken ? getPriceToken(data.token.address) : undefined;

  const secondRow = (() => {
    switch (data.token.type) {
      case 'ERC-20': {
        const tokenDecimals = Number(data.token.decimals) || 18;
        const text = `${ BigNumber(data.value).dividedBy(10 ** tokenDecimals).dp(8).toFormat() } ${ data.token.symbol || '' }`;
        const price = priceToken ? `$(${ (BigNumber(data.value).dividedBy(10 ** tokenDecimals).dp(8).toNumber() * priceToken.perToken).toLocaleString() })` : undefined;

        return (
          <>
            <TruncatedValue value={ text } price={ price }/>
            { data.token.exchange_rate && <chakra.span ml={ 2 }>@{ Number(data.token.exchange_rate).toLocaleString() }</chakra.span> }
          </>
        );
      }
      case 'ERC-721': {
        const text = `${ BigNumber(data.value).toFormat() } ${ data.token.symbol || '' }`;
        const price = priceToken ? `($${ (BigNumber(data.value).toNumber() * priceToken.perToken).toLocaleString() })` : undefined;
        return <TruncatedValue value={ text } price={ price }/>;
      }
      case 'ERC-1155': {
        return (
          <>
            <chakra.span textOverflow="ellipsis" overflow="hidden" mr={ 6 }>
              #{ data.token_id || 0 }
            </chakra.span>
            <span>
              { BigNumber(data.value).toFormat() }
            </span>
          </>
        );
      }
      case 'ERC-404': {
        return (
          <>
            { data.token_id !== null && (
              <chakra.span textOverflow="ellipsis" overflow="hidden" mr={ 6 }>
                #{ data.token_id || 0 }
              </chakra.span>
            ) }
            { data.value !== null && (
              <span>
                { data.token.decimals ?
                  getCurrencyValue({ value: data.value, decimals: data.token.decimals, accuracy: 2 }).valueStr :
                  BigNumber(data.value).toFormat()
                }
              </span>
            ) }
          </>
        );
      }
    }
  })();

  const url = route({ pathname: '/token/[hash]', query: { hash: data.token.address } });

  return (
    <LinkInternal
      px={ 1 }
      py="10px"
      display="flex"
      flexDir="column"
      rowGap={ 2 }
      borderColor="divider"
      borderBottomWidth="1px"
      _hover={{
        bgColor: useColorModeValue('blue.50', 'gray.800'),
      }}
      color="unset"
      fontSize="sm"
      href={ url }
    >
      <Flex alignItems="center" w="100%" overflow="hidden">
        <TokenEntity
          token={ data.token }
          noSymbol
          noCopy
          noLink
          fontWeight={ 700 }
          mr={ 2 }
        />
        { (data.usd || priceToken) && (
          <TruncatedValue
            value={ `$${ data.usd && data.usd.gt(ZERO) ? data.usd.toFormat(2) : priceToken?.perToken.toLocaleString() }` }
            fontWeight={ 700 }
            minW="120px"
            ml="auto"
            textAlign="right"
          />
        ) }
      </Flex>
      <Flex alignItems="center" justifyContent="space-between" w="100%" whiteSpace="nowrap">
        { secondRow }
      </Flex>
    </LinkInternal>
  );
};

export default React.memo(TokenSelectItem);
